import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

// Stripe price IDs from setup
const PRICE_MONTHLY = 'price_1TNZyA87Dutzt6pBFHImKzdT'
const PRICE_ANNUAL = 'price_1TNZyA87Dutzt6pBdO9ETkXz'

export default function Signup() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [plan, setPlan] = useState<'monthly' | 'annual'>('monthly')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const urlPlan = searchParams.get('plan')
    const urlEmail = searchParams.get('email')
    
    if (urlPlan === 'monthly' || urlPlan === 'annual') {
      setPlan(urlPlan)
    }
    
    if (urlEmail) {
      setEmail(urlEmail)
    } else {
      const storedEmail = localStorage.getItem('ith_lead_email')
      if (storedEmail) setEmail(storedEmail)
    }
    
    const storedName = localStorage.getItem('ith_lead_name')
    const storedPhone = localStorage.getItem('ith_lead_phone')
    if (storedName) setFullName(storedName)
    if (storedPhone) setPhone(storedPhone)
  }, [searchParams])

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // First check if user already exists by trying to sign in
      const { data: existingUser } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (existingUser.user) {
        // User exists and password is correct - redirect to dashboard
        navigate('/listings')
        return
      }

      // User doesn't exist or wrong password - try to create
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName || email.split('@')[0],
          }
        }
      })

      if (authError) {
        if (authError.message.includes('already registered') || authError.message.includes('already exists')) {
          setError('An account with this email already exists. Please sign in instead.')
        } else {
          setError(authError.message)
        }
        setLoading(false)
        return
      }

      if (!authData.user) {
        setError('Failed to create account. Please try again.')
        setLoading(false)
        return
      }

      // Manually create user profile if trigger didn't work
      // Silent fail if profile already exists (created by trigger)
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert({
          id: authData.user.id,
          email: email,
          full_name: fullName || email.split('@')[0],
          role: 'agent'
        })

      if (profileError && !profileError.message.includes('duplicate')) {
        console.error('Profile creation error:', profileError)
      }

      // Link to Carol Royse Team (ignore errors if already linked)
      try {
        await supabase.from('team_members').insert({
          team_id: 'ef6b2a6d-0237-4fb3-ad09-15dba4bf7fb2',
          user_id: authData.user.id,
          role: 'agent'
        })
      } catch {
        // Ignore duplicate errors
      }

      // Store for checkout
      localStorage.setItem('ith_pending_user_id', authData.user.id)
      localStorage.setItem('ith_pending_email', email)

      // Create Stripe checkout
      const response = await fetch('https://insidethishome-api.royal-grass-dbfc.workers.dev/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price_id: plan === 'monthly' ? PRICE_MONTHLY : PRICE_ANNUAL,
          user_id: authData.user.id,
          email: email,
          full_name: fullName,
          phone: phone,
          success_url: `${window.location.origin}/post-payment`,
          cancel_url: `${window.location.origin}/signup?canceled=true`,
        }),
      })

      const result = await response.json()

      if (!response.ok || result.error) {
        setError(result.error || 'Failed to create checkout session')
        setLoading(false)
        return
      }

      window.location.href = result.url
    } catch (err: any) {
      console.error('Signup error:', err)
      setError(err.message || 'An unexpected error occurred. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">InsideThisHome</h1>
          <p className="mt-2 text-gray-600">Complete your registration</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSignup}>
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Selected Plan</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setPlan('monthly')}
                className={`p-4 border-2 rounded-lg text-left ${
                  plan === 'monthly' ? 'border-primary bg-primary/5' : 'border-gray-200'
                }`}
              >
                <div className="font-semibold">Monthly</div>
                <div className="text-2xl font-bold">$49<span className="text-sm font-normal">/mo</span></div>
              </button>
              <button
                type="button"
                onClick={() => setPlan('annual')}
                className={`p-4 border-2 rounded-lg text-left ${
                  plan === 'annual' ? 'border-primary bg-primary/5' : 'border-gray-200'
                }`}
              >
                <div className="font-semibold">Annual</div>
                <div className="text-2xl font-bold">$490<span className="text-sm font-normal">/yr</span></div>
                <div className="text-xs text-green-600">Save 2 months</div>
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Full Name</label>
            <input
              id="fullName"
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone</label>
            <input
              id="phone"
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">Create Password</label>
            <input
              id="password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 6 characters"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {loading ? 'Creating account...' : 'Continue to Payment'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-600">
          Already have an account?{' '}
          <a href="/login" className="text-primary hover:text-primary-dark">Sign in</a>
        </p>
      </div>
    </div>
  )
}